/**
 * Global click instrumentation (ALP-32444).
 *
 * Hand-instrumenting every control across eighteen modules is a losing race: the modules that most
 * need measuring are the ones nobody remembers to annotate, and a new page starts life invisible.
 * One delegated listener covers all of them at once and keeps covering new ones for free.
 *
 * This is a SAFETY NET, not a replacement for a deliberate event. `ui.click` says which control was
 * pressed; it cannot say whether the export succeeded or how many rows came back. Where the outcome
 * matters, call `track()` explicitly — the two coexist happily.
 *
 * Two things it deliberately does NOT do:
 *  - It never reads text from arbitrary elements. Only controls (button / link / MDS button / an
 *    explicit data-track) are considered, because a table cell's text is business data and has no
 *    place in telemetry.
 *  - It never reports a control it cannot name. An unnamed icon button would arrive as an empty
 *    string and pile up into a meaningless top row.
 */
import { useEffect } from "react";
import { track } from "./track";
import { AnalyticsAction, moduleFromPath } from "./taxonomy";

/** Controls worth reporting. Anything else is ignored outright. */
const CONTROL_SELECTOR = [
  "[data-track]",
  "button",
  "a[href]",
  "mc-button",
  "mc-tab",
  '[role="button"]',
  '[role="tab"]',
].join(",");

const MAX_LABEL_LENGTH = 60;

/**
 * Same control clicked twice inside this window counts once. Double-clicks and MDS web components
 * that re-emit through their shadow host would otherwise double every number.
 */
const DEDUPE_WINDOW_MS = 400;

/**
 * Control labels are a queryable dimension, so anything inside one becomes a row on a chart.
 *
 * Some are built from data: removing a recipient chip in Tax Invoice produced labels like
 * "Remove susan.shan.chen@maersk.com" — a colleague's address, recorded per click, answering no
 * question anyone asked. The shared mailbox a user is working in is worth measuring, but it is
 * already on every event as a URL parameter, so nothing is lost by keeping addresses out of here.
 */
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

export function redactEmails(label: string): string {
  return label.replace(EMAIL_PATTERN, "(email)");
}

/**
 * Name a control the way a person would read it. MDS components carry their text in a `label`
 * attribute rather than as child text, so `textContent` alone silently misses most of the portal.
 */
function labelFor(element: Element): string {
  const explicit =
    element.getAttribute("data-track") ||
    element.getAttribute("aria-label") ||
    element.getAttribute("label") ||
    element.getAttribute("title");
  if (explicit && explicit.trim()) {
    return redactEmails(explicit.trim()).slice(0, MAX_LABEL_LENGTH);
  }

  // MDS's React wrapper sets `label` as a DOM PROPERTY, not an attribute. `<McButton
  // label="Apply" />` renders as a literally empty `<mc-button>`: no label attribute, no text.
  // Reading only attributes and textContent makes every button written that way look unnamed,
  // and the guard below then drops it — silently, forever. Verified against the real DOM.
  const propertyLabel = (element as unknown as { label?: unknown }).label;
  if (typeof propertyLabel === "string" && propertyLabel.trim()) {
    return redactEmails(propertyLabel.trim()).slice(0, MAX_LABEL_LENGTH);
  }

  // An MDS tooltip renders its content TWICE: once inside `slot="trigger"`, which is the part the
  // user sees, and once as the tooltip body. Both are in the DOM, so reading the whole subtree
  // returns the label doubled — the Email Workbench mailbox tabs reported
  // "CUSLCL.SZH@LnS.maersk.com CUSLCL.SZH@LnS.maersk.com", splitting one mailbox across two rows
  // on every chart that groups by click label. Read the trigger subtree, which is the label.
  const tooltipTrigger = element.querySelector('mc-tooltip [slot="trigger"]');
  const source = (tooltipTrigger ?? element) as HTMLElement;

  const text = source.innerText || source.textContent || "";
  const cleaned = text.replace(/\s+/g, " ").trim();
  return redactEmails(cleaned).slice(0, MAX_LABEL_LENGTH);
}

/** A control's own kind, so "which buttons" and "which links" stay separable. */
function controlKind(element: Element): string {
  const tag = element.tagName.toLowerCase();
  if (tag === "a") return "link";
  if (tag === "mc-tab" || element.getAttribute("role") === "tab") return "tab";
  return "button";
}

/**
 * Mount once, in `_app`. Uses capture so a handler that calls stopPropagation — common in modals
 * and menus — cannot hide the interaction from telemetry.
 */
export function useInteractionTracking(): void {
  useEffect(() => {
    const recent = new Map<string, number>();

    const onClick = (event: MouseEvent) => {
      try {
        const target = event.target as Element | null;
        if (!target || typeof target.closest !== "function") return;

        const control = target.closest(CONTROL_SELECTOR);
        if (!control) return;

        const label = labelFor(control);
        // Unnamed control: reporting it would create a nameless bucket nobody can act on.
        if (!label) return;

        const now = Date.now();
        const key = `${label}|${controlKind(control)}`;
        const last = recent.get(key);
        if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return;
        recent.set(key, now);
        // The map is per-mount and keyed by label, so it is bounded by the number of distinct
        // controls in the app — no cleanup sweep needed.

        track(moduleFromPath(window.location.pathname), AnalyticsAction.UiClick, {
          target: label,
          control: controlKind(control),
        });
      } catch {
        // Telemetry must never break a click.
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}
